import { formatDate, summarizeChecklist } from "../utils/formatters";
import { summarizeSellerPortfolio } from "../utils/sellerSteps";
import { EXPORT_FOOTER_DISCLAIMER } from "../legal/copy.js";

export const REPORT_DISCLAIMER = `DreamHome Journey communication record for retention and broker supervision. ${EXPORT_FOOTER_DISCLAIMER}`;

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function reportFileStem(address) {
  return String(address || "communication")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48) || "communication";
}

function partyName(user) {
  if (!user) return "";
  return user.name || user.email || "";
}

function senderName(senderId, parties) {
  const match = [parties.broker, parties.agent, parties.client].find((person) => person?.id === senderId);
  return match?.name || "Someone";
}

function formatStamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)} · ${date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

export function buildReportModel(payload = {}, extras = {}) {
  const transaction = payload.transaction || {};
  const parties = payload.parties || {};
  const threads = payload.threads || [];
  const timeline = threads
    .flatMap((thread) =>
      (thread.messages || []).map((message) => ({
        at: message.timestamp,
        stamp: formatStamp(message.timestamp),
        threadId: thread.id,
        senderId: message.sender_id,
        senderName: senderName(message.sender_id, parties),
        text: message.message_text || "",
        attachments: message.attachments || [],
      }))
    )
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));

  const attachments = (payload.attachments || []).length
    ? payload.attachments
    : timeline.flatMap((item) =>
        (item.attachments || []).map((file) => ({
          ...file,
          message_stamp: item.stamp,
          senderName: item.senderName,
        }))
      );

  const checklist = payload.checklist || {};
  const checklistSections =
    checklist.type === "seller"
      ? summarizeSellerPortfolio(checklist.portfolio || {})
      : summarizeChecklist(checklist.preferences || {});

  return {
    title: "DreamHome Journey",
    subtitle: "Communication report",
    property: transaction.property_address || "Conversation record",
    status: transaction.status || "open",
    clientRole: transaction.client_role || "",
    generatedAt: formatStamp(payload.generated_at) || formatStamp(new Date().toISOString()),
    exportType: extras.exportType || payload.export_type || "",
    generatedBy: extras.generatedBy || "",
    parties: {
      broker: partyName(parties.broker),
      agent: partyName(parties.agent),
      client: partyName(parties.client),
    },
    partyEmails: {
      broker: parties.broker?.email || "",
      agent: parties.agent?.email || "",
      client: parties.client?.email || "",
    },
    timeline,
    attachments: attachments.map((file) => ({
      name: file.file_name || "Attachment",
      type: file.mime_type || "",
      stamp: file.message_stamp || formatStamp(file.uploaded_at),
      senderName: file.senderName || "",
    })),
    checklistType: checklist.type || "buyer",
    checklistSections,
    disclaimer: REPORT_DISCLAIMER,
  };
}
